import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { getMaxArrayLength, isNotEmptyArray } from './utilsHelper';
import { createToaster } from '@meforma/vue-toaster';
import { CURRENT_DATE_CELL } from '../data/constants/ordersExecutionReportHeaders';
import {
  ORDERS_EXECUTION_REPORT_COLUMN_WIDTHS,
  ORDERS_EXECUTION_REPORT_HEADERS
} from '../data/constants/ordersExecutionReportHeaders';

const YELLOW_COLOR = 'ffffcc';
const BLUE_COLOR = '9dc3e6';
const ORANGE_COLOR = 'bf9000';
const LIGHT_ORANGE_COLOR = 'fff2cc';
const GREEN_COLOR = '92d050';
const GRAY_COLOR_FOR_TEXT = 'b7b7b7';
const NON_VALID_ORDER_COLOR = 'd96868';
const WRAP_DATA_ROWS = true;

export function generateReport(reportData, worksheetName = 'Worksheet') {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(worksheetName);
  const headers = ORDERS_EXECUTION_REPORT_HEADERS;
  const firstDataRowNumber = 5;

  const rows = [];

  const maxVerticalCellsInHeaders = getMaxArrayLength(headers);

  for (let i = 0; i < maxVerticalCellsInHeaders; i++) {
    rows.push([]);
  }

  if (!isNotEmptyArray(reportData?.orders)) {
    const toaster = createToaster({ position: 'top-right' });
    toaster.show('Brak danych, nie można wygenerować raportu.', {
      duration: 3000,
      type: 'warning'
    });

    return;
  }

  // Fill with headers
  for (let i = 0; i < headers.length; i++) {
    if (!Array.isArray(headers[i])) {
      rows[0][i] = headers[i];
      continue;
    }

    for (let j = 0; j < headers[i].length; j++) {
      rows[j][i] = headers[i][j];
    }
  }

  worksheet.addRows(rows);

  // Fill with the filters
  const filtersRowData = [];

  for (let i = 1; i <= headers.length; i++) {
    filtersRowData.push(i);
  }

  worksheet.addRow(filtersRowData);

  // Fill with the data
  const dataRows = [];
  const boldCells = [];
  const grayFontCells = [];
  const nonValidOrdersRows = [];

  for (let i = 0; i < reportData.orders.length; i++) {
    let orderData = reportData.orders[i].columns;
    const count = reportData.orders[i].count;

    for (let j = 0; j < count; j++) {
      const row = [];
      const isFirstOrderRow = j === 0;

      for (let k = 0; k < orderData.length; k++) {
        const orderColumnData = orderData[k];

        if (Array.isArray(orderColumnData)) {
          row.push(orderColumnData[j] ?? '');
        } else if (isFirstOrderRow) {
          row.push(orderColumnData);
          boldCells.push([dataRows.length, k + 1]);
        } else {
          // if P column (index 15), skip for other rows than first
          if (k + 1 != 16) {
            row.push(orderColumnData);
            grayFontCells.push([dataRows.length, k + 1]);
          }
        }
      }

      dataRows.push(row);

      if (reportData.orders[i].is_valid === 0) {
        nonValidOrdersRows.push(dataRows.length);
      }
    }
  }

  worksheet.addRows(dataRows);
  worksheet.getCell(CURRENT_DATE_CELL).value += ` ${reportData.date_now}`;

  // Bold cells
  boldCells.forEach((boldCell) => {
    worksheet.getCell(firstDataRowNumber + boldCell[0], boldCell[1]).font = {
      name: 'Calibri',
      bold: true
    };
  });

  // Gray font cells
  grayFontCells.forEach((grayFontCell) => {
    worksheet.getCell(firstDataRowNumber + grayFontCell[0], grayFontCell[1]).font = {
      name: 'Calibri',
      color: { argb: GRAY_COLOR_FOR_TEXT }
    };
  });

  worksheet.mergeCells('A1:I2');
  worksheet.mergeCells('J1:N2');
  worksheet.mergeCells('O1:Q2');
  worksheet.mergeCells('R1:R3');
  worksheet.mergeCells('S1:S3');

  // Format cells
  for (let i = 1; i <= 4; i++) {
    worksheet.getRow(i).font = { name: 'Calibri', size: 11, bold: true };
    worksheet.getRow(i).alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
  }

  if (WRAP_DATA_ROWS) {
    for (let i = firstDataRowNumber; i <= worksheet.rowCount; i++) {
      worksheet.getRow(i).alignment = { wrapText: true };
    }
  }

  // Widths
  ORDERS_EXECUTION_REPORT_COLUMN_WIDTHS.forEach((element, index) => {
    worksheet.getColumn(index + 1).width = element;
  });

  // Colors
  worksheet.getCell('A1').fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: YELLOW_COLOR }
  };

  worksheet.getCell('J1').fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: BLUE_COLOR }
  };

  worksheet.getCell('O1').fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: ORANGE_COLOR }
  };

  worksheet.getCell('R1').fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: GREEN_COLOR }
  };

  worksheet.getCell('S1').fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: GREEN_COLOR }
  };

  for (let i = 1; i <= 19; i++) {
    worksheet.getCell(3, i).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: GREEN_COLOR }
    };
  }

  for (let i = 1; i <= 19; i++) {
    worksheet.getCell(4, i).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: i > 9 ? LIGHT_ORANGE_COLOR : YELLOW_COLOR }
    };
  }

  for (let y = firstDataRowNumber; y <= worksheet.rowCount; y++) {
    for (let x = 1; x <= 19; x++) {
      worksheet.getCell(y, x).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: x > 9 ? LIGHT_ORANGE_COLOR : YELLOW_COLOR }
      };
    }
  }

  // Borders
  for (let i = 0; i < worksheet.columnCount; i++) {
    for (let row = 2; row <= worksheet.rowCount; row++) {
      worksheet.getCell(row, i + 1).border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
    }
  }

  // Non valid order color
  nonValidOrdersRows.forEach((rowNumber) => {
    for (let i = 1; i <= worksheet.columnCount; i++) {
      worksheet.getCell(rowNumber + firstDataRowNumber - 1, i).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: NON_VALID_ORDER_COLOR }
      };
    }
  });

  worksheet.autoFilter = {
    from: { row: maxVerticalCellsInHeaders + 1, column: 1 },
    to: { row: worksheet.rowCount, column: headers.length }
  };

  // Export xlsx
  workbook.xlsx
    .writeBuffer()
    .then((buffer) =>
      saveAs(
        new Blob([buffer]),
        `Raport z realizacji zleceń - ${new Date().toLocaleDateString()}.xlsx`
      )
    )
    .catch((err) => console.log('generateReport(): Error writing excel export.', err));
}
